import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CouponListItem, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-coupons',
  imports: [AinPageComponent, AinStateComponent, TableModule, ButtonModule, AinDatePipe],
  template: `
    <ain-page [title]="t('sales.coupons.title')" [description]="t('sales.coupons.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="selectedRows.set($event ?? [])">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.coupons.fields.code') }}</th>
                <th>{{ t('sales.coupons.fields.discountType') }}</th>
                <th>{{ t('sales.coupons.fields.discountValue') }}</th>
                <th>{{ t('sales.coupons.fields.redemptions') }}</th>
                <th>{{ t('sales.coupons.fields.validFrom') }}</th>
                <th>{{ t('sales.coupons.fields.validTo') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td>{{ row.code }}</td>
                <td>{{ row.discountType }}</td>
                <td>{{ row.discountValue }}</td>
                <td>{{ row.maxRedemptions != null ? row.redemptionCount + ' / ' + row.maxRedemptions : row.redemptionCount }}</td>
                <td>{{ row.validFromUtc | ainDate }}</td>
                <td>{{ row.validToUtc ? (row.validToUtc | ainDate) : '-' }}</td>
                <td>{{ row.isActive ? t('sales.coupons.active') : t('sales.coupons.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .error { color: #b91c1c; }
  `]
})
export class SalesCouponsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<CouponListItem[]>([]);
  readonly selectedRows = signal<CouponListItem[]>([]);

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.api.listCoupons().subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.coupons.errorLoad')); this.loading.set(false); }
    });
  }

  openCreate(): void { void this.router.navigate(['/sales/coupons/new']); }
  openSelected(): void { const row = this.selectedRows()[0]; if (row) void this.router.navigate(['/sales/coupons', row.id]); }
  t(key: string): string { return this.localization.translate(key); }
}
